import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Building, Calendar, TrendingUp } from 'lucide-react';

const caseStudyIcons = [Building, Calendar, TrendingUp];

const FilloutPortfolioSection = () => {
  const { t } = useTranslation('fillout');

  const caseStudiesData = t('portfolio.caseStudies', { returnObjects: true });
  const caseStudies = Array.isArray(caseStudiesData) ? caseStudiesData as Array<{
    title: string;
    client: string;
    challenge: string;
    solution: string;
    results: string[];
    tags: string[];
  }> : [];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {t('portfolio.title')}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground">
            {t('portfolio.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {caseStudies.map((caseStudy, index) => {
            const IconComponent = caseStudyIcons[index];
            
            return (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 bg-background/80 backdrop-blur-sm border-primary/10 hover:border-primary/30">
                <CardHeader className="space-y-3 sm:space-y-4 p-4 sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base sm:text-lg group-hover:text-primary transition-colors">
                        {caseStudy.title}
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {caseStudy.client}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {caseStudy.tags.map((tag, tagIndex) => (
                      <Badge key={tagIndex} variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Challenge</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {caseStudy.challenge}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-2">Solution</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {caseStudy.solution}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-3">Results</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, resultIndex) => (
                        <li key={resultIndex} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-muted-foreground">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FilloutPortfolioSection;